import React, { useEffect, useRef, useState } from 'react';

import { CloseOutlined, MailFilled, MenuOutlined, UserOutlined } from '@ant-design/icons';
import { notification } from 'antd';
import { Avatar, Badge, Button, Dropdown, Flex, Image, Layout, message, Space } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';
import { WarningOutlined } from '@ant-design/icons';
import eCoachVMSApi from '../Api';
import Sidebar from '../components/Sidebar';
import { useNotifications } from '../hooks/useNotifications';
import { useSound } from '../hooks/useSound';
import LogoIcon from '../images/logo-icon.png';
import { logoutUser, selectUser } from '../store/reducers/sessionReducer';
import { selectUserSettings } from '../store/reducers/sessionReducer';
import { load, setLoading } from '../store/reducers/sessionReducer';
import useScreenSize from '../utils/useScreenSize';
import './mainlayout.css';
import Clock from '../components/Clock';

const { Header, Content } = Layout;

const MainLayout = () => {
    const [collapsed, setCollapsed] = useState(false);

    const { isMobile, isDesktop, isTabletToMobile } = useScreenSize();
    const [open, setOpen] = useState(false);
    const user = useSelector(selectUser);
    const userSettings = useSelector(selectUserSettings);
    const navigate = useNavigate();
    const [isBarHidden, setIsBarHidden] = useState(false);
    const { baseURL } = eCoachVMSApi();
    const [notifications, setNotifications] = useState([]);
    const unsubscribe = useRef(null);
    const playNotificationSound = useSound('/notification.mp3');
    const dispatch = useDispatch();

    // Determine readiness safely
    const hasToggles = !!userSettings?.custom_toggles;
    const hasWebsocketFlag = hasToggles && Object.prototype.hasOwnProperty.call(userSettings.custom_toggles, 'use_websocket');
    const settingsReady = hasToggles;
    const wsFlag = hasWebsocketFlag ? !!userSettings.custom_toggles.use_websocket.value : false;

    // Add this temporary debug log ↓↓↓
    console.log('[MainLayout] user.id', user?.id, 'settingsReady', settingsReady, 'wsFlag', wsFlag);

    // Always start polling; switch to socket only if explicitly allowed
    const { subscribe } = useNotifications(user, wsFlag, settingsReady);

    useEffect(() => {
        if (!user?.id) return;
        unsubscribe.current = subscribe((notification) => {
            setNotifications((prev) => [notification, ...prev]);
            showNotification({
                id: notification.id,
                title: notification.title,
                message: notification.message,
                link_url: notification.link_url,
                sender: notification.sender
            });
            updateDeliveredStatus(notification.id);

            const fetchNotifications = async () => {
                try {
                    const notification = await baseURL.get(`/notifications/user/?user_id=${user.id}`);
                    setNotifications(notification.data);
                } catch (error) {
                    console.error(error);
                }
            };
            fetchNotifications();
        });

        return () => {
            if (unsubscribe.current) unsubscribe.current();
        };
    }, [user?.id, subscribe]);

    const markNotificationAsRead = async (id) => {
        await baseURL.patch(`/notifications/${id}/read`, { is_read: true });
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
        message.success('Notification marked as read!');
    };

    const updateDeliveredStatus = async (id, is_delivered = true) => {
        await baseURL.patch(`/notifications/${id}/delivered`, { is_delivered: is_delivered });
    };

    const handleDeleteNotification = async (id) => {
        try {
            await baseURL.delete(`/notifications/${id}`);

            setNotifications((prevNotifications) => prevNotifications.filter((notification) => notification.id !== id));

            console.log('Notification deleted successfully');
            message.success('Notification deleted!');
        } catch (error) {
            console.error('Failed to delete notification:', error);
        }
    };

    const showNotification = (notificationData) => {
        console.log(notificationData);
        const key = `notification-${Date.now()}`;

        // Play notification sound if enabled in user settings
        if (userSettings?.custom_toggles?.notification_sound?.value ?? true) {
            playNotificationSound();
        }

        // Detect ghost user and hide "From" line for it
        const showSender =
            notificationData.sender && !notificationData.sender.includes('998877') && !notificationData.sender.toLowerCase().includes('ghost');

        notification.open({
            key,
            message: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px', fontSize: '24px' }}>
                    <WarningOutlined className="alertIcon" />
                    <b style={{ fontSize: '18px', color: '#8b0000', lineHeight: 1 }}>{notificationData.title}</b>
                </div>
            ),
            description: (
                <div>
                    <div dangerouslySetInnerHTML={{ __html: notificationData.message }} />

                    {notificationData.link_url && (
                        <div style={{ color: '#1890ff', marginTop: '6px' }}>
                            <a href={notificationData.link_url}>{notificationData.link_url}</a>
                        </div>
                    )}

                    {showSender && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginTop: '20px',
                                fontSize: '12px',
                                color: '#444'
                            }}>
                            <span>Sent by: {notificationData.sender}</span>
                            <Button
                                type="link"
                                size="small"
                                onClick={async () => {
                                    await markNotificationAsRead(notificationData.id);
                                    notification.destroy(key);
                                }}>
                                Mark as Read
                            </Button>
                        </div>
                    )}
                </div>
            ),
            placement: 'topRight',
            duration: 5,
            showProgress: true
        });
    };

    const handleLogout = async () => {
        if (unsubscribe.current) {
            unsubscribe.current();
            unsubscribe.current = null;
        }

        const session = localStorage.getItem('session') || sessionStorage.getItem('session');
        const isWorkshop = session?.startsWith('workshop') || false;
        const redirectTo = isWorkshop ? '/service' : '/login';
        await dispatch(logoutUser()).unwrap();
        navigate(redirectTo);
    };

    const handleSettings = () => {
        navigate('/settings/user-settings');
    };

    const userMenuItems = [
        { key: '1', label: 'Profile' },
        { key: '2', label: 'Settings', onClick: handleSettings },
        { key: '3', label: 'Logout', onClick: handleLogout }
    ];

    const toggleClickMenu = () => {
        if (isDesktop === true) {
            setCollapsed(!collapsed);
        } else if (isTabletToMobile === true) {
            setOpen(true);
        } else if (isMobile === true) {
            setOpen(true);
        } else {
            setCollapsed(!collapsed);
        }
    };

    const onClose = () => {
        setOpen(false);
    };

    const unreadCount = notifications.filter((notif) => !notif.is_read).length;

    const items = notifications.map((notification) => ({
        key: notification.id,
        label: (
            <div
                className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                onClick={() => !notification.is_read && markNotificationAsRead(notification.id)}
                style={{
                    padding: '8px',
                    borderLeft: `3px solid ${!notification.is_read ? '#1890ff' : '#d9d9d9'}`
                }}>
                <div>
                    <div
                        style={{
                            fontWeight: !notification.is_read ? 'bold' : 'normal',
                            marginBottom: '10px'
                        }}>
                        {notification.title}
                    </div>

                    <div style={{ fontSize: '12px', color: '#666' }} dangerouslySetInnerHTML={{ __html: notification.message }}></div>

                    {notification.link_url && (
                        <div style={{ fontSize: '11px', color: '#1890ff', marginTop: '4px' }}>
                            <a href={notification.link_url}>{notification.link_url}</a>
                        </div>
                    )}
                </div>

                <CloseOutlined
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteNotification(notification.id);
                    }}
                />
            </div>
        )
    }));

    const dropdownRender = () => (
        <div
            style={{
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                padding: '8px 0',
                left: isMobile ? 50 : 0,
                position: 'relative',
                width: isMobile ? '255px' : '300px'
            }}>
            <div
                style={{
                    padding: '8px 16px',
                    borderBottom: '1px solid #f0f0f0',
                    fontWeight: 'bold'
                }}>
                Notifications ({notifications.length})
            </div>
            {notifications.length > 0 ? (
                items.map((item) => (
                    <div key={item.key} style={{ padding: '0 16px' }}>
                        {item.label}
                    </div>
                ))
            ) : (
                <div style={{ padding: '16px', textAlign: 'center', color: '#999' }}>No notifications</div>
            )}
        </div>
    );

    return (
        <Layout style={{ minHeight: '100vh' }}>
            {!isBarHidden && <Sidebar collapsed={collapsed} open={open} onClose={onClose} />}
            <Layout>
                <Header
                    style={{
                        background: '#3b8eb9',
                        padding: isTabletToMobile ? '0' : '0 20px',
                        display: 'flex',
                        justifyContent: isTabletToMobile ? '' : 'space-between',
                        alignItems: 'center',
                        height: '51px',
                        lineHeight: 'inherit'
                    }}>
                    {isTabletToMobile ? (
                        <Flex
                            align="center"
                            justify="center"
                            style={{
                                padding: '5px 10px',
                                backgroundColor: '#3382b0',
                                textAlign: 'center',
                                minHeight: '51px',
                                marginRight: 6
                            }}>
                            <Image src={LogoIcon} preview={false} style={{ height: '32px', objectFit: 'contain' }} />
                        </Flex>
                    ) : (
                        ''
                    )}
                    {!isBarHidden && (
                        <Button
                            className="humberge-btn"
                            type="text"
                            icon={<MenuOutlined style={{ fontSize: 20 }} />}
                            onClick={toggleClickMenu}
                            style={{
                                color: 'white',
                                padding: 0,
                                backgroundColor: 'transparent'
                            }}
                        />
                    )}
                    {isBarHidden && <div></div>}

                    <Flex
                        gap={14}
                        align="center"
                        style={{
                            paddingRight: isTabletToMobile ? '15px' : '',
                            marginLeft: isTabletToMobile ? 'auto' : ''
                        }}>
                        {!isMobile && <Clock />}

                        <Dropdown dropdownRender={dropdownRender} trigger={['click']} placement="bottomRight" className="notification-dropdown">
                            <Space>
                                <Badge count={unreadCount}>
                                    <MailFilled style={{ color: 'white', cursor: 'pointer' }} />
                                </Badge>
                            </Space>
                        </Dropdown>

                        <Dropdown
                            menu={{ items: userMenuItems }}
                            placement="bottomRight"
                            trigger={['click']}
                            dropdownRender={(menu) => (
                                <div
                                    style={{
                                        width: '150px',
                                        position: 'relative',
                                        top: 0
                                    }}>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '-8px',
                                            right: '8px',
                                            width: 0,
                                            height: 0,
                                            borderLeft: '8px solid transparent',
                                            borderRight: '8px solid transparent',
                                            borderBottom: '8px solid white', // Match dropdown background
                                            zIndex: 1
                                        }}
                                    />
                                    {menu}
                                </div>
                            )}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    cursor: 'pointer'
                                }}>
                                <Avatar icon={<UserOutlined />} />
                                <span style={{ color: 'white' }}>{user?.name}</span>
                                <span style={{ color: 'white' }}>{user?.role}</span>
                            </div>
                        </Dropdown>
                    </Flex>
                </Header>
                <Content className="mainlayout" style={{ backgroundColor: '#ffffff' }}>
                    <Outlet />
                </Content>
            </Layout>
        </Layout>
    );
};

export default MainLayout;
